/**
 * Job-related TypeScript types for the frontend
 * These types extend the API client types with additional statuses needed for the ops workflow
 */

import { JobStatus, FilingType as ApiFilingType } from '@taxassist/api-client';

// Re-export JobStatus from API client for consistency
export { JobStatus };

// Create a unified FilingType that includes both API names and friendly names
export const FilingType = {
  // API client names
  ...ApiFilingType,
  // Friendly names for backward compatibility
  PERSONAL: "1040",
  FIDUCIARY: "1041", 
  PARTNERSHIP: "1065",
  CORPORATION: "1120",
  S_CORPORATION: "1120S",
} as const;

export type FilingType = typeof FilingType[keyof typeof FilingType];

export enum FilingStatus {
  Single = 'Single',
  MarriedFilingJointly = 'Married Filing Jointly',
  MarriedFilingSeparately = 'Married Filing Separately',
  HeadOfHousehold = 'Head of Household',
  QualifyingWidowOrWidower = 'Qualifying Widow(er)',
}

export interface UploadedFile {
  fileName: string;
  s3Key: string;
  uploadedAt: string;
  fileSize?: number;
  contentType?: string;
  submittedToSurePrep?: boolean;
}

export interface SurePrepDocument {
  documentId: string;
  fileName: string;
  uploadedAt: string;
  status: string;
}


export interface JobRecord {
  jobId: string;
  firmId: string;
  createdBy: string;
  filingType: FilingType;
  taxYear: number;
  clientName: string;
  clientNumber: string;
  description?: string;
  metadata?: Record<string, any>;
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
  uploadedFiles?: UploadedFile[];
  binderId?: string;
  binderName?: string;
  surePrepDocuments?: SurePrepDocument[];
  processedEventIds?: string[];
  errorMessage?: string;
  
  // Credit tracking fields
  creditCharged?: boolean;
  creditChargedDate?: string;
  
  // Triage system fields
  assignedReviewer?: string;
  assignedAt?: string;
  etaNum?: number; // Unix timestamp (as number) of delivery deadline
  
  // Additional fields for UI display (populated by API)
  firmName?: string;
  clientId?: string;
  submittedAt?: string;
  priority?: 'HIGH' | 'NORMAL' | 'LOW';
}

export interface OpsJobsQueueRequest {
  page?: number;
  pageSize?: number;
  priority?: 'HIGH' | 'NORMAL' | 'LOW';
  firmId?: string;
  ageMinDays?: number;
  status?: typeof JobStatus.TRIAGE | 'ALL';
  sortBy?: 'priority' | 'age' | 'eta';
  sortOrder?: 'asc' | 'desc';
}

export interface OpsJobsQueueResponse {
  jobs: JobRecord[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface AssignJobRequest {
  jobId: string;
}

export interface AssignJobResponse {
  success: boolean;
  message?: string;
}
